/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { get, action, computed } from '@ember/object';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  generateArrivalRequestParams,
  validateArrival,
} from 'linced-app/utils/nz-moe/arrival';
import State from 'linced-app/enums/enrolment-request-state';
import Resource from 'linced-app/enums/enrolment-resource';
import JurisdictionalStatus from 'linced-app/enums/jurisdictional-status';
import EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import config from 'linced-app/config/environment';
import { observes } from '@ember-decorators/object';

export default class ArrivalUpload extends Component {
  // Services
  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  students;

  State = State;

  @optionAlias('nzMOE:schoolType:type')
  schoolType;

  @tracked validationInProgress = true;
  @tracked attendance = null;

  @computed(
    'person.{gender,dob,currentYearLevel,jurisdictionalId}',
    'enrolment',
    'attendance',
  )
  get uploadValidationErrors() {
    return validateArrival(
      this.person,
      this.enrolment,
      this.attendance,
      this.schoolType,
    );
  }

  @tracked checkingStatus = false;
  arrival = null;
  status = null;

  @computed(
    'uploadValidationErrors.length',
    'checkingStatus',
    'validationInProgress',
    'uploading',
  )
  get canUpload() {
    if (this.validationInProgress || this.checkingStatus || this.uploading) {
      // Validation, status check and/or upload in progress; don't allow upload.
      return false;
    }
    if (this.get('uploadValidationErrors.length') > 0) {
      // Not valid; don't allow upload.
      return false;
    }
  }

  @tracked uploading = false;

  @computed('arrival.nzMoe.type')
  get uploaded() {
    let type = this.get('arrival.nzMoe.type');
    // A `J` (success) or `O` (already enrolled at calling school) response
    // is considered a successful upload.
    return type === 'J' || type === 'O';
  }

  uploadErrors = null;
  @tracked
  detailsUpdating = false;
  @tracked
  detailsUpdated = false;

  @tracked
  pollTimer;

  didReceiveAttrs() {
    // Perform attendance validation for this learner.
    let params = {
      schoolId: this.get('session.activeSchool'),
      arrivals: [
        {
          personId: this.get('person.id'),
          entryDate: this.get('enrolment.entryDate'),
        },
      ],
    };
    let options = {
      data: JSON.stringify(params),
    };
    let url = `${config.APP.apiHost}/attendance/v4/arrivals/nzmoe/:validate`;
    this.fetch.post(url, options).then((response) => {
      let results = get(response, 'results') ?? [];
      let attendance = findBy(results, 'personId', this.get('person.id'));
      this.attendance = attendance;
      this.validationInProgress = false;
    });

    // Trigger check for an in-flight upload.
    this.checkStatus();

    return super.didReceiveAttrs(...arguments);
  }

  checkStatus() {
    this.checkingStatus = true;
    let params = {
      jurisdiction: Jurisdiction.NZ_MOE,
      personId: this.person.id,
      personEnrolmentId: this.enrolment.id,
    };
    return this.store
      .query('enrolments/enrolment', params)
      .then((arrivals) => {
        if (!isEmpty(arrivals)) {
          // We have at least one Enrolment (arrival) response.
          let successful =
            findBy(arrivals, 'nzMoe.type', 'J') ||
            findBy(arrivals, 'nzMoe.type', 'O');
          if (!isEmpty(successful)) {
            // We have a J (success) or O (already enrolled at calling school) response.
            // Set uploaded flag so we don't/can't try again.
            this.setProperties({
              arrival: successful,
            });
            // Since this student is marked as attending in ENROL,
            // update their jurisdictional enrolment status to match.
            return this.markUploaded();
          }
        }

        // No existing "successy" Enrolment (arrival) records.
        // Check for in-flight Status requests.
        params.status = State.PROCESSING;
        params.resource = Resource.ENROLMENTS;
        return this.store
          .query('enrolments/status', params)
          .then((statuses) => {
            if (!isEmpty(statuses)) {
              // We got at least one Status.
              // We should usually only have one in-flight Status request at most,
              // so just pick the first one returned.
              this.set('status', statuses[0]);
            }
          });
      })
      .finally(() => {
        this.checkingStatus = false;
        // Update pending upload counts.
        this.students.updatePendingUploads();
      });
  }

  markUploaded() {
    return this.enrolment
      .reload()
      .then((enrolment) => {
        if (
          enrolment.jurisdictionalStatus ===
          JurisdictionalStatus.PENDING_ENROLMENT
        ) {
          let newStatus = JurisdictionalStatus.ENROLLED;
          if (enrolment.status === EnrolmentStatus.FORMER) {
            // Student has already unenrolled; skip "enrolled" jurisdictional status.
            newStatus = JurisdictionalStatus.PENDING_UNENROLMENT;
          }
          enrolment.set('jurisdictionalStatus', newStatus);
          return enrolment.save();
        }
        return Promise.resolve();
      })
      .then(() => {
        this.paperToaster.show(
          `${this.person.fullName}'s arrival has been uploaded to ENROL successfully.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );
      })
      .catch(() => {
        this.paperToaster.show(
          `Error updating ${this.person.fullName}'s ENROL status.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  removeNSN() {
    return this.person.reload().then((person) => {
      person.get('fields.nzMOE:nsn').set('value', '');
      person.set('jurisdictionalId', '');
      return person.save();
    });
    // NOTE: No toasts on this, as it's not something which the user requested/expects.
  }

  @observes('status', 'arrival')
  pollStatus() {
    if (!isEmpty(this.status)) {
      if (isEmpty(this.arrival)) {
        // We have a Status (request), but not yet an Enrolment (arrival) response.
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
      } else if (!isEmpty(this.pollTimer)) {
        // We've got an Enrolment (arrival); cancel any pending status poll.
        cancel(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }

  updateStatus() {
    if (isEmpty(this.status)) {
      // If we don't have a Status record, we can't poll it for updates.
      return;
    }
    this.status.reload().then((status) => {
      let resourceId = status.get('resourceIds.firstObject');
      switch (status.status) {
        case State.READY:
          // Processing complete - fetch the Enrolment (arrival) record.
          if (!isEmpty(resourceId)) {
            this.store
              .findRecord('enrolments/enrolment', resourceId)
              .then((arrival) => {
                this.setProperties({
                  arrival,
                  uploading: false,
                });
                switch (arrival.get('nzMoe.type')) {
                  case 'J':
                  case 'O':
                    // We got a "successy" response.
                    this.markUploaded();
                    break;

                  case 'G':
                    // NSN not found.
                    this.removeNSN();
                    break;
                }
              });
            // Update pending upload counts.
            this.students.updatePendingUploads();
          }
          break;

        case State.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          break;
      }
    });
  }

  @action
  upload() {
    this.uploading = true;
    let params = generateArrivalRequestParams(
      this.person,
      this.enrolment,
      this.session,
    );
    let status = this.store.createRecord('enrolments/status', {
      schoolId: this.session.school,
      requesterId: this.session.person,
      jurisdiction: Jurisdiction.NZ_MOE,
      resource: Resource.ENROLMENTS,
      nzMoe: [params],
    });
    this.setProperties({
      status,
      arrival: null,
    });
    status.save();
  }

  @action
  updateDetails(differences) {
    this.detailsUpdating = true;
    this.detailsUpdated = false;
    return this.person
      .reload()
      .then((person) => {
        differences.forEach((difference) => {
          person.get(`fields.${difference.key}`).set('value', difference.enrol);
        });
        return person.save();
      })
      .then(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = true;
      })
      .catch(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = false;

        this.paperToaster.show(
          `Error updating ${this.person.fullName}'s details.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }
}
